import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { MapSection } from "@/components/MapSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Phone, Mail, MapPin } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { getRecaptchaToken } from "@/hooks/useRecaptcha";
import { projectDetailsSchema } from "@/lib/projectDetails";


export default function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [showPrivacyError, setShowPrivacyError] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    message: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) return; // bot detected
    const details = projectDetailsSchema.safeParse(formData.message);
    if (!details.success) {
      toast.error(details.error.issues[0]?.message || "Please tell us about your project.");
      return;
    }
    if (!privacyConsent) {
      setShowPrivacyError(true);
      return;
    }
    setIsSubmitting(true);

    try {
      const recaptchaToken = await getRecaptchaToken("submit_lead");
      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error } = await supabase.functions.invoke("submit-lead", {
        body: { ...formData, source: "Contact Page", privacyConsent, smsConsent, recaptchaToken },
      });

      if (error) throw error;

      toast.success("Thank you! We'll get back to you within 24 hours.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        state: "",
        zipCode: "",
        message: "",
      });
      setPrivacyConsent(false);
      setSmsConsent(false);
      setShowPrivacyError(false);
    } catch (err) {
      console.error("Form submission error:", err);
      toast.error("Something went wrong. Please call or text us at (973) 725-2395.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Masonry Pro Totowa, NJ | Masonry Pro Construction</title>
        <meta name="description" content="Call or text (973) 725-2395 for a free estimate. Masonry Pro Construction serves Totowa and North Jersey. Patios, walls, concrete, waterproofing. No trip fees." />
        <meta property="og:title" content="Contact Masonry Pro Totowa, NJ | Masonry Pro Construction" />
        <meta property="og:description" content="Call or text (973) 725-2395 for a free estimate. Masonry Pro Construction serves Totowa and North Jersey. Patios, walls, concrete, waterproofing. No trip fees." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://masonry-pro.com/og/services.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
      </Helmet>

      <Header />

      <main className="min-h-screen bg-background pt-32 md:pt-36">
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 max-w-6xl mx-auto items-start">
              {/* Contact Form */}
              <div className="bg-card rounded-xl p-8 md:p-10 border border-border">
                <h1 className="text-2xl md:text-3xl font-bold mb-2">
                  Need a Mason Contractor in{" "}
                  <span className="text-gradient-gold">Totowa, NJ</span> or North Jersey? Request an Estimate
                </h1>
                <p className="text-muted-foreground mb-8">
                  Fill out the form and we'll get back to you promptly.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Honeypot field - hidden from humans */}
                  <div className="absolute opacity-0 top-0 left-0 h-0 w-0 -z-10" aria-hidden="true">
                    <label htmlFor="contact-website">Website</label>
                    <input
                      id="contact-website"
                      name="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="name" className="sr-only">
                      Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Name*"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="bg-white text-black border-border h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="sr-only">
                      Email
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Email*"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="bg-white text-black border-border h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="sr-only">
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="Phone Number*"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-white text-black border-border h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="address" className="sr-only">Address</Label>
                    <Input
                      id="address"
                      name="address"
                      type="text"
                      placeholder="Address*"
                      required
                      value={formData.address}
                      onChange={handleChange}
                      className="bg-white text-black border-border h-12"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="city" className="sr-only">City</Label>
                      <Input
                        id="city"
                        name="city"
                        type="text"
                        placeholder="City*"
                        required
                        value={formData.city}
                        onChange={handleChange}
                        className="bg-white text-black border-border h-12"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state" className="sr-only">State</Label>
                      <Input
                        id="state"
                        name="state"
                        type="text"
                        placeholder="State*"
                        required
                        value={formData.state}
                        onChange={handleChange}
                        className="bg-white text-black border-border h-12"
                      />
                    </div>
                    <div>
                      <Label htmlFor="zipCode" className="sr-only">Zip Code</Label>
                      <Input
                        id="zipCode"
                        name="zipCode"
                        type="text"
                        placeholder="Zip Code*"
                        required
                        value={formData.zipCode}
                        onChange={handleChange}
                        className="bg-white text-black border-border h-12"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="sr-only">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Message*"
                      required
                      maxLength={500}
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      className="bg-white text-black border-border resize-none"
                    />
                  </div>

                  {/* Privacy Policy Consent */}
                  <div className="space-y-1">
                    <div className="flex items-start gap-2">
                      <Checkbox
                        id="contact-privacy-consent"
                        checked={privacyConsent}
                        onCheckedChange={(checked) => {
                          setPrivacyConsent(checked === true);
                          if (checked) setShowPrivacyError(false);
                        }}
                        className="mt-1"
                      />
                      <Label htmlFor="contact-privacy-consent" className="text-sm text-muted-foreground font-normal leading-snug cursor-pointer">
                        I agree to the{" "}
                        <Link to="/privacy-policy" className="text-secondary underline hover:text-secondary/80">
                          Privacy Policy
                        </Link>{" "}
                        and consent to being contacted by Masonry Pro Construction regarding my inquiry.
                      </Label>
                    </div>
                    {showPrivacyError && (
                      <p className="text-sm text-destructive ml-6">
                        You must agree to our Privacy Policy to submit this request. Without this we are unable to contact you regarding your inquiry.
                      </p>
                    )}
                  </div>

                  {/* SMS Consent */}
                  <div className="space-y-1">
                    <div className="flex items-start gap-2">
                      <Checkbox
                        id="contact-sms-consent"
                        checked={smsConsent}
                        onCheckedChange={(checked) => setSmsConsent(checked === true)}
                        className="mt-1"
                      />
                      <Label htmlFor="contact-sms-consent" className="text-sm text-muted-foreground font-normal leading-snug cursor-pointer">
                        I consent to receive text messages from Masonry Pro Construction regarding my estimate and project updates. Message and data rates may apply. Reply STOP to opt out.
                      </Label>
                    </div>
                    {!smsConsent && (
                      <p className="text-xs text-muted-foreground/70 ml-6">
                        Note: Without SMS consent we will only be able to reach you by phone call or email.
                      </p>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground text-center">* Required. Good details now mean an honest, accurate estimate later.</p>
                  <Button
                    type="submit"
                    variant="gold"
                    size="lg"
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending..." : "SEND"}
                  </Button>

                  <p className="text-xs text-muted-foreground/60 text-center leading-snug">
                    This site is protected by reCAPTCHA and the Google{" "}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-muted-foreground">Privacy Policy</a> and{" "}
                    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-muted-foreground">Terms of Service</a> apply.
                  </p>
                </form>
              </div>

              {/* Contact Info */}
              <div className="lg:pt-4">
                <h2 className="text-2xl md:text-3xl font-bold mb-8">
                  Contact <span className="text-gradient-gold">Information</span>
                </h2>

                <div className="space-y-6">
                  <a
                    href="tel:+19737252395"
                    className="flex items-center gap-4 group"
                  >
                    <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-secondary/20 transition-colors">
                      <Phone className="w-5 h-5 text-secondary" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground group-hover:text-secondary transition-colors">
                        Call or Text
                      </p>
                      <p className="text-muted-foreground">(973) 725-2395</p>
                    </div>
                  </a>

                  <a
                    href="mailto:masonryproconstruction@aol.com"
                    className="flex items-center gap-4 group"
                  >
                    <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-secondary/20 transition-colors">
                      <Mail className="w-5 h-5 text-secondary" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground group-hover:text-secondary transition-colors">
                        Email Us
                      </p>
                      <p className="text-muted-foreground">
                        masonryproconstruction@aol.com
                      </p>
                    </div>
                  </a>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-secondary" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">Our Location</p>
                      <p className="text-muted-foreground">
                        36 Highview Ave, Totowa, NJ 07512
                      </p>
                    </div>
                  </div>
                </div>

                {/* Service Area Note */}
                <div className="mt-8 p-6 bg-card rounded-xl border border-border">
                  <h3 className="font-semibold text-lg mb-2">Service Area</h3>
                  <p className="text-muted-foreground text-sm">
                    We proudly serve Essex, Passaic, Bergen, Morris, and Hudson
                    Counties throughout North New Jersey.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <MapSection />
      <Footer />
    </>
  );
}
